import ReactGA from 'react-ga4';
import * as Sentry from '@sentry/react';
import { CONSENT_POLICY_VERSION, CONSENT_STORAGE_KEY } from './constants';
import { ConsentCategories, StoredConsentRecord } from './types';

export const getStoredConsent = (): StoredConsentRecord | null => {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      parsed.policyVersion === CONSENT_POLICY_VERSION &&
      parsed.categories &&
      typeof parsed.categories.analytics === 'boolean' &&
      typeof parsed.categories.performanceDiagnostics === 'boolean'
    ) {
      return parsed as StoredConsentRecord;
    }
    return null;
  } catch (e) {
    return null;
  }
};

export const saveStoredConsent = (categories: ConsentCategories): StoredConsentRecord => {
  const record: StoredConsentRecord = {
    policyVersion: CONSENT_POLICY_VERSION,
    updatedAt: new Date().toISOString(),
    categories: {
      analytics: Boolean(categories.analytics),
      performanceDiagnostics: Boolean(categories.performanceDiagnostics)
    }
  };
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
  } catch (e) {
    console.error('Failed to save consent choice to localStorage', e);
  }
  return record;
};

export const clearGaCookies = () => {
  if (typeof document === 'undefined') return;
  const cookies = document.cookie.split(';');
  const domain = window.location.hostname;
  const domainParts = domain.split('.');

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;

    if (name === '_ga' || name.startsWith('_ga_')) {
      // Clear cookie for root path and potential domain scopes
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain};`;
      if (domainParts.length > 1) {
        const rootDomain = '.' + domainParts.slice(-2).join('.');
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${rootDomain};`;
      }
    }
  }
};

let isGaInitialized = false;

export const applyConsentEffects = (categories: ConsentCategories) => {
  const gaId = import.meta.env.VITE_GA_MEASUREMENT_ID;

  // 1. GA4 Sync
  if (categories.analytics) {
    if (gaId) {
      if (typeof window !== 'undefined') {
        // @ts-ignore
        window[`ga-disable-${gaId}`] = false;
      }
      if (!isGaInitialized) {
        ReactGA.initialize([
          {
            trackingId: gaId,
            gaOptions: { cookieFlags: 'SameSite=None; Secure', debug_mode: true },
            gtagOptions: { debug_mode: true }
          }
        ]);
        isGaInitialized = true;
      }
    }
  } else {
    if (gaId && typeof window !== 'undefined') {
      // @ts-ignore
      window[`ga-disable-${gaId}`] = true;
    }
    clearGaCookies();
  }

  // 2. Sentry Replay & Tracing Sync
  try {
    const replay = Sentry.getReplay();
    if (replay) {
      if (categories.performanceDiagnostics) {
        replay.start();
      } else {
        replay.stop();
      }
    }
  } catch (e) {
    // Sentry may not be active in dev/local environments
  }
};
